import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import styles from './styles/Greetings.module.scss';

import { OmaLummeApp } from '../../omaLummeContext';

import Register from './Register';
import Button from '../Common/Button';
import Loading from '../Common/Loading';
import Tabs from '../Common/Tabs';
import LoginIframe from './LoginIframe';

// Types.
import { SubLandingPageProps } from '../../types/customer';
// Images.
import logo from '../../logo.svg';
import sunflower from '../../images/sunflower.jpg';
// Utils.
import { gaEventIfAllowed } from '../../utils/googleAnalytics';

const Greetings: React.FunctionComponent<SubLandingPageProps> = (props: SubLandingPageProps) => {
  const { t } = useTranslation();
  const { users } = OmaLummeApp.useState();
  const { activeUser } = users;

  const { from } = props;
  const navigate = useNavigate();

  let userIdentified = false;
  if (activeUser.userName !== '') {
    userIdentified = true;
  }

  const [activeTab, setActiveTab] = React.useState('login');

  const loginRegisterTabs = [
    {
      label: t('common:register.loginLink'),
      key: 'login',
    },
    {
      label: t('common:register.registerLink'),
      key: 'register',
    },
  ];

  React.useEffect(() => {
    if (userIdentified && from) {
      navigate(from);
    }
  }, [userIdentified, from]);

  const randomString = sessionStorage.getItem('csrf') ?? (Math.random() + 1).toString(36).substring(2);
  const token = sessionStorage.getItem('token');

  const sessionId =
    localStorage.getItem('sessionId') ??
    Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  localStorage.setItem('sessionId', sessionId);
  sessionStorage.setItem('csrf', randomString);

  const userHasToken = sessionStorage.getItem('token');

  return (
    <div className={`${styles.greetings} ${userIdentified ? styles.identified : styles.unidentified}`}>
      <div className={`lumme-box ${styles.loginRegister}`}>
        <img src={logo} className={styles.logo} alt="Lumme Energia" />
        <Tabs tabs={loginRegisterTabs} active={{ activeTab, setActiveTab }} block />

        <div aria-live="polite" aria-atomic="true" aria-relevant="all">
          {activeTab === 'login' && (
            <>
              <h1>{t('common:greetings.loginMessage')}</h1>
              {userHasToken && <Loading />}
              {!userHasToken && !token && <LoginIframe randomString={randomString} />}
              {import.meta.env.VITE_URL_LOGIN_STRONG_AUTH && (
                <div className={styles.strongAuth}>
                  <label>{t('common:register.loginStrongAuthIdentify')}</label>
                  <Button
                    className="buttonWhite"
                    link={`${import.meta.env.VITE_URL_LOGIN_STRONG_AUTH}&state=${randomString}`}
                    onClick={(): void => {
                      gaEventIfAllowed({
                        category: 'Login sivun klikkaukset',
                        action: 'Tunnistaudu pankkitunnuksilla',
                      });
                    }}
                    label={t(`common:register.loginStrongAuthBankCredsAriaTitle`)}
                  >
                    {t('common:register.loginStrongAuthBankCreds')}
                  </Button>
                  <p>{t('common:register.loginStrongAuthNotice')}</p>
                </div>
              )}
              <p className={styles.loginLinks}>
                <a
                  href={`${import.meta.env.VITE_ALTERNATIVE_LOGIN_URL}&state=${randomString}`}
                  onClick={(): void => {
                    gaEventIfAllowed({
                      category: 'Login sivun klikkaukset',
                      action: 'Etkö näe kirjautumislomaketta',
                    });
                  }}
                >
                  {t('landingPage:login.formNotVisible')}
                </a>
                <br />
                <a
                  href={import.meta.env.VITE_URL_FORGOT_PASSWORD}
                  onClick={(): void => {
                    gaEventIfAllowed({
                      category: 'Login sivun klikkaukset',
                      action: 'Unohtuiko salasana',
                    });
                  }}
                >
                  {t('landingPage:login.forgotPW')}
                </a>
                <br />
                <a
                  href={import.meta.env.VITE_URL_REGISTER}
                  onClick={(): void => {
                    gaEventIfAllowed({
                      category: 'Login sivun klikkaukset',
                      action: 'Eikö sinulla ole Lummetunnusta',
                    });
                  }}
                >
                  {t('landingPage:login.noAccount')}
                </a>
                <br />
                <Link to="/customerService">{t('landingPage:login.faqLink')}</Link>
              </p>
            </>
          )}
          {activeTab === 'register' && <Register />}
        </div>
      </div>

      <div className={styles.sunflower}>
        <img src={sunflower} alt="" />
      </div>
    </div>
  );
};

export default Greetings;
